import { useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { API_CONFIG } from '@/config/constants';
import { Logo } from '@/components/Logo';

type DeletionStatus = 'pending' | 'completed' | 'failed';

interface DeletionStatusResponse {
  status: DeletionStatus;
  requested_at: string;
  completed_at: string | null;
}

/**
 * Public status page for a Meta data deletion request (A4.4).
 *
 * Reads `?code=<uuid>` from the URL and calls
 * GET /legal/meta-deletion-status/{code}. The endpoint has no auth dep, so we
 * use a bare axios call rather than the apiClient interceptor chain to avoid
 * accidentally piping a stale Flouri token to a public endpoint. Unknown,
 * malformed, and never-existed codes all 404 — same UX, no info leak.
 *
 * Reachability for ≥30 days from request is a Meta contract requirement.
 */
export function MetaDeletionStatus() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');

  const { data, isLoading, isError, error } = useQuery<DeletionStatusResponse>({
    queryKey: ['meta-deletion-status', code],
    queryFn: async () => {
      // encodeURIComponent: `code` is user-supplied via ?code=…; reserved
      // characters (/, ?, #) in the raw value would otherwise rewrite the
      // request URL. The backend additionally validates as UUID, so a
      // properly-encoded non-UUID will round-trip to a 404 — correct UX.
      const safeCode = encodeURIComponent(code ?? '');
      const resp = await axios.get<DeletionStatusResponse>(
        `${API_CONFIG.BASE_URL}/legal/meta-deletion-status/${safeCode}`,
      );
      return resp.data;
    },
    enabled: Boolean(code),
    retry: false,
    staleTime: 30_000,
  });

  const notFound =
    isError && axios.isAxiosError(error) && error.response?.status === 404;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" aria-label="Flouri home">
            <Logo />
          </Link>
          <Link
            to="/legal/meta-data-deletion"
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            About Meta data deletion
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-semibold text-gray-900 mb-4">
          Meta Data Deletion Status
        </h1>

        {!code && (
          <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4 text-yellow-900">
            <p>
              No deletion code provided. The URL should look like
              <code className="ml-1 px-1 bg-yellow-100 rounded">
                /legal/meta-deletion-status?code=&lt;your-code&gt;
              </code>
              . If you've lost your code, email{' '}
              <a
                href="mailto:support@flouri.ai"
                className="text-blue-600 hover:text-blue-800"
              >
                support@flouri.ai
              </a>
              .
            </p>
          </div>
        )}

        {code && isLoading && (
          <div className="text-gray-700">Checking status…</div>
        )}

        {code && notFound && (
          <div className="rounded-md bg-gray-100 border border-gray-200 p-4 text-gray-800">
            <h2 className="font-semibold mb-1">No record found</h2>
            <p>
              We could not find a deletion request matching this code. If you
              believe this is in error, email{' '}
              <a
                href="mailto:support@flouri.ai"
                className="text-blue-600 hover:text-blue-800"
              >
                support@flouri.ai
              </a>{' '}
              with the code.
            </p>
          </div>
        )}

        {code && isError && !notFound && (
          <div className="rounded-md bg-red-50 border border-red-200 p-4 text-red-900">
            <h2 className="font-semibold mb-1">
              We couldn't check the status right now
            </h2>
            <p>Please refresh the page in a few minutes.</p>
          </div>
        )}

        {data && (
          <div className="rounded-md bg-white border border-gray-200 p-6">
            <StatusBadge status={data.status} />
            <dl className="mt-4 space-y-2 text-sm text-gray-700">
              <div className="flex justify-between">
                <dt className="font-medium">Requested at</dt>
                <dd>{new Date(data.requested_at).toLocaleString()}</dd>
              </div>
              {data.completed_at && (
                <div className="flex justify-between">
                  <dt className="font-medium">Completed at</dt>
                  <dd>{new Date(data.completed_at).toLocaleString()}</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="font-medium">Confirmation code</dt>
                <dd className="font-mono text-xs">{code}</dd>
              </div>
            </dl>
            {data.status === 'pending' && (
              <p className="mt-4 text-sm text-gray-600">
                Deletion typically completes in seconds. If this still shows
                "pending" after a few minutes, email support.
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function StatusBadge({ status }: { status: DeletionStatus }) {
  const config: Record<
    DeletionStatus,
    { label: string; className: string }
  > = {
    pending: {
      label: 'Pending',
      className: 'bg-yellow-100 text-yellow-900 border-yellow-200',
    },
    completed: {
      label: 'Completed',
      className: 'bg-green-100 text-green-900 border-green-200',
    },
    failed: {
      label: 'Failed',
      className: 'bg-red-100 text-red-900 border-red-200',
    },
  };
  const { label, className } = config[status];
  return (
    <span
      className={`inline-block px-3 py-1 rounded-full text-sm font-semibold border ${className}`}
    >
      {label}
    </span>
  );
}
